import { useState, useCallback } from "react";
import type { FormData, LessonStep, GeneratedLesson } from "../types";
import {
  DEFAULT_LESSON_TYPE,
  DEFAULT_DURATION,
  DEFAULT_DIFFICULTY,
  DEFAULT_STUDENTS_COUNT,
} from "../constants";

interface UseLessonFormReturn {
  step: LessonStep;
  formData: FormData;
  generatedLesson: GeneratedLesson | null;
  error: string;
  loadingMessage: string;
  handleChange: (field: keyof FormData, value: any) => void;
  setStep: (step: LessonStep) => void;
  setGeneratedLesson: (lesson: GeneratedLesson | null) => void;
  setError: (error: string) => void;
  setLoadingMessage: (message: string) => void;
  resetForm: () => void;
}

export function useLessonForm(initialData?: {
  topic?: string;
  subject?: string;
  grade?: string;
}): UseLessonFormReturn {
  const [step, setStep] = useState<LessonStep>("form");
  const [generatedLesson, setGeneratedLesson] =
    useState<GeneratedLesson | null>(null);
  const [error, setError] = useState<string>("");
  const [loadingMessage, setLoadingMessage] = useState(
    "Preparando requisição...",
  );

  const [formData, setFormData] = useState<FormData>({
    title: initialData?.topic ? `Aula sobre ${initialData.topic}` : "",
    subject: initialData?.subject || "",
    grade: initialData?.grade || "",
    topic: initialData?.topic || "",
    objectives: "",
    lessonType: DEFAULT_LESSON_TYPE,
    duration: DEFAULT_DURATION,
    difficulty: DEFAULT_DIFFICULTY,
    studentsCount: DEFAULT_STUDENTS_COUNT,
    includeActivities: true,
    includeMaterials: true,
    includeAssessment: true,
    customInstructions: "",
  });

  const handleChange = useCallback((field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      title: "",
      subject: "",
      grade: "",
      topic: "",
      objectives: "",
      lessonType: DEFAULT_LESSON_TYPE,
      duration: DEFAULT_DURATION,
      difficulty: DEFAULT_DIFFICULTY,
      studentsCount: DEFAULT_STUDENTS_COUNT,
      includeActivities: true,
      includeMaterials: true,
      includeAssessment: true,
      customInstructions: "",
    });
    setGeneratedLesson(null);
    setError("");
    setStep("form");
  }, []);

  return {
    step,
    formData,
    generatedLesson,
    error,
    loadingMessage,
    handleChange,
    setStep,
    setGeneratedLesson,
    setError,
    setLoadingMessage,
    resetForm,
  };
}
